<template>
  <div>
  <va-modal
    id="requestDetailsModal"
    ref="requestDetailsModal"
    :value="isOpen"
    size="medium"
    @cancel="onCancel"
    :hideDefaultActions="true"
  >
    <div slot="header" class="modal-title d-flex justify-content-between mb-4">
      <span>{{ $t('evocall.requests.details_title') }}</span>
    </div>

    <div class="modal-header-actions d-flex justify-content-end">
        <va-popover
          offset="50"
          placement="bottom"
          :popoverStyles="{'border-radius': 0}"
          :backgroundColor="priorities[request.priority.id].color"
        >
          <va-button
            class="va-button-rounded priority-icon-wrapper"
            :class="`priority--${request.priority.name.toLowerCase()}`"
            @click="onClickUpdatePriority"
          >
            <span class="is-bold">{{ $t('evocall.requests.priority')}}: {{ $t(`evocall.priorities.${request.priority.id}`, request.priority.name) }}</span>
          </va-button>

          <PriorityDetails
            slot="popover"
            :request="request"
          />
        </va-popover>

        <va-popover
          v-if="request.obs"
          placement="bottom"
          :message="request.obs"
        >
          <va-button
            large
            class="va-button-rounded"
            color="gray"
            icon="i-has-comment-accent"
            @click="onClickUpdateObservations"
          >
          </va-button>
        </va-popover>

        <va-button
          v-else
          large
          class="va-button-rounded"
          color="gray"
          icon="i-add-comment-white"
          @click="onClickUpdateObservations"
        >
        </va-button>

        <va-popover
          placement="bottom"
          :message="$t('evocal.requests.cancel_request')"
          v-if="userCanCancelRequest"
        >
          <va-button
            large
            class="va-button-rounded"
            icon="i-phone-slash-white"
            color="gray"
            @click="onClickCancelRequest"
          >
          </va-button>
        </va-popover>
    </div>

    <fade-transition :duration="250" mode="out-in">
      <div v-if="!appointment" class="py-5 mt-3 text-center" key="loading-wrapper">
        <span class="is-bold"><b>{{$t('agenda.statistics.loading')}}</b></span>
      </div>

      <div v-if="appointment" key="appointment-info-wrapper">
        <AppointmentInfo
          key="appointment-info"
          :appointment="appointment"
          :service-slug="serviceSlug"
          @closeAppointmentDetailsModal="closeAppointmentDetailsModal"
        />

        <va-button
          large
          id="btn-see-appointment-details"
          class="btn-gray px-4 py-3"
          @click="onClickisShowingAppointmentAnamnesisDetails"
        >
          {{$t('evocall.requests.see_appointment_details')}}
        </va-button>
      </div>
    </fade-transition>

    <div slot="actions" v-if="request && userCanAcceptRequest">
      <div class="d-flex flex-column align-items-end">
        <va-button
          large
          class="px-4 py-3"
          @click="submit">
            {{ $t('evocall.requests.start') }}
        </va-button>
        <div>
          <small v-if="inBreak">{{ $t('evocall.requests.break_override_warning_1') }}</small>
          <small v-if="inBreak">{{ $t('evocall.requests.break_override_warning_2') }}</small>
        </div>
      </div>
    </div>
    <div v-if="isShowingAppointmentAnamnesisDetails">
      <AppointmentAnamnesisModal
        :appointment="parentAppointment"
        :request="request"
        @modalClosed="closeAppointmentDetails"
        :service-slug="serviceSlug"
      />
    </div>
  </va-modal>

    <ObservationsForm
      v-if="isUpdatingObservations"
      :request="request"
      @modal-closed="onObservationsModalClosed"
      @observations-updated="onObservationsUpdated"
    />

    <CancelCallRequestModal
      v-if="isCancelingRequest"
      :request="request"
      :service="authClinicalService"
      @request-canceled="onRequestCanceled"
      @operation-not-permited="onRequestCancelOperationNotPermited"
      @modal-closed="onCancelRequestModalClosed"
    />
  </div>

</template>
<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { RequestService } from '@/api/EvocallService.js'
import { AppointmentService } from '@/api/AuthClinicalService'
import FadeTransition from '@/components/common/transitions/FadeTransition'
import ObservationsForm from '@/components/evocall/requests/ObservationsForm'
import AppointmentInfo from '@/components/clinical/appointments/AppointmentInfo'
import CancelCallRequestModal from '@/components/clinical/calls/CancelCallRequestModal'
import AppointmentAnamnesisModal from '@/components/clinical/appointments/AppointmentAnamnesisModal'

export default {
  components: {
    PriorityForm,
    FadeTransition,
    PriorityDetails,
    AppointmentInfo,
    ObservationsForm,
    CancelCallRequestModal,
    AppointmentAnamnesisModal,
  },
  props: {
    request: {
      type: Object,
      required: true
    },
    serviceSlug: {
      type: String,
      required: true
    },
    component: {
      type: Object,
      required: true
    }
  },
  computed: {
    ...mapGetters('auth', [
      'userIsActiveAudiologist'
    ]),
    waitingRoomModuleKey: function () {
      return `waiting-room-${this.authClinicalService.slug}`
    },
    ...mapState({
      activeRequest (state, getters) {
        return getters[`${this.waitingRoomModuleKey}/activeRequest`] || null
      },
      evocallInfoTypes (state) {
        return state[this.waitingRoomModuleKey].evocallInfoTypes
      },
      authClinicalInfoTypes (state) {
        return state[this.waitingRoomModuleKey].authClinicalInfoTypes
      },
      accessTypes (state) {
        return state[this.waitingRoomModuleKey].accessTypes
      },
      inBreak (state, getters) {
        return getters[`${this.waitingRoomModuleKey}/inBreak`]
      },
      requests (state, getters) {
        return getters[`${this.waitingRoomModuleKey}/requests`]
      },
      priorities (state) {
        return state[this.waitingRoomModuleKey].priorities || {}
      }
    }),
    authClinicalService: function () {
      return this.$store.state.services.find(s => s.id === this.request.service_id)
    },
    evocallService: function () {
      return this.$store.state.services.find(s => s.url === this.$store.state.evocall_server)
    },
    userCanUpdateRequestPriority: function () {
      return this.evocallService.user_permissions.some(permission => permission.info_type === this.evocallInfoTypes.REQUESTS && permission.access_type >= this.accessTypes.ADMIN && (permission.entity_id === null || permission.entity_id === this.request.entity_id))
    },
    userCanCancelRequest: function () {
      return this.authClinicalService !== null && this.evocallService !== null &&
      this.authClinicalService.user_permissions.some(permission => permission.info_type === this.authClinicalInfoTypes.APPOINTMENT_DETAILS && permission.access_type >= this.accessTypes.WRITE && (permission.entity_id === null || permission.entity_id === this.request.entity_id)) &&
      this.evocallService.user_permissions.some(permission => permission.info_type === this.evocallInfoTypes.REQUESTS && permission.access_type >= this.accessTypes.ADMIN && (permission.entity_id === null || permission.entity_id === this.request.entity_id))
    },
    useCanUpdateRequest: function () {
      return this.evocallService.user_permissions.some(p => (p.entity_id === null || p.entity_id === this.request.entity_id) && p.access_type >= this.accessTypes.WRITE && p.info_type === this.evocallInfoTypes.REQUESTS)
    },
    userCanAcceptRequest: function () {
      return this.useCanUpdateRequest && this.userIsActiveAudiologist && (!this.activeRequest || (this.activeRequest && !this.activeRequest.call))
    }
  },
  data () {
    return {
      appointment: null,
      parentAppointment: null,
      isOpen: false,
      acceptedCall: false,
      wasCanceledByUser: false,
      isUpdatingPriority: false,
      isUpdatingObservations: false,
      isCancelingRequest: false,
      isShowingAppointmentAnamnesisDetails: false
    }
  },
  created () {
    this.loadAppointmentDetails()
    this.loadAuthUserEntityActivityRecords()
  },
  mounted () {
    this.isOpen = true
  },
  methods: {
    closeAppointmentDetailsModal: function () {
      this.isOpen = false
    },
    ...mapActions('auth', {
      loadAuthUserEntityActivityRecords: 'LOAD_AUTH_USER_ENTITY_ACTIVITIES'
    }),
    onClickUpdatePriority: function () {
      if (this.userCanUpdateRequestPriority) {
        this.isOpen = false
        setTimeout(() => {
          this.isUpdatingPriority = true
        }, 500)
      }
    },
    onClickUpdateObservations: function () {
      this.isOpen = false
      setTimeout(() => {
        this.isUpdatingObservations = true
      }, 500)
    },
    onClickCancelRequest: function () {
      this.isOpen = false
      setTimeout(() => {
        this.wasCanceledByUser = true
        this.isCancelingRequest = true
      }, 500)
    },
    onPriorityModalClosed: function () {
      this.isUpdatingPriority = false
      this.isOpen = true
    },
    onObservationsModalClosed: function () {
      this.isUpdatingObservations = false
      this.isOpen = true
    },
    onCancelRequestModalClosed: function () {
      this.isCancelingRequest = false
      this.wasCanceledByUser = false
      this.isOpen = true
    },
    onPriorityUpdated: function (eventPayload) {
      this.$emit('priority-updated', eventPayload)
    },
    onObservationsUpdated: function (eventPayload) {
      this.$emit('observations-updated', eventPayload)
    },
    onRequestCanceled: function (eventPayload) {
      this.closeModal()
    },
    onRequestCancelOperationNotPermited: function () {
      this.closeModal()
    },
    loadParentAppointment: function (appointmentId) {
      if (this.authClinicalService) {
        return new Promise((resolve, reject) => {
          AppointmentService.get(this.authClinicalService.url, appointmentId)
            .then(appointment => {
              this.parentAppointment = appointment
              resolve(appointment)
            })
            .catch(error => reject(error))
        })
      }
    },
    onClickisShowingAppointmentAnamnesisDetails: function () {
      this.loadParentAppointment(this.appointment.parent.id)
        .then(() => {
          this.isShowingAppointmentAnamnesisDetails = true
        })
        .catch(error => console.log(error))
    },
    closeAppointmentDetails: function () {
      this.isShowingAppointmentAnamnesisDetails = false
    },
    loadAppointmentDetails: function () {
      AppointmentService.get(this.authClinicalService.url, this.request.appointment_id)
        .then(appointment => {
          this.appointment = {
            ...appointment,
            parent: {
              ...appointment.parent,
              start: this.$options.filters.localTime(appointment.parent.start),
              end: this.$options.filters.localTime(appointment.parent.end)
            }
          }
        }).catch(errors => {
          console.log('errors', errors)
        })
    },
    onCancel: function () {
      this.closeModal()
    },
    submit: function () {
      const wasInBreak = this.inBreak
      if (!this.acceptedCall) {
        this.acceptedCall = true
        RequestService.accept(this.request.id)
          .then(request => {
            this.showToast(wasInBreak ? this.$t('evocall.requests.break_overridden') : this.$t('evocall.requests.actions.accepted'), this.$store.state.successToastOptions)
            this.closeModal()
            setTimeout(() => {
              // let routeData = this.$router.resolve({ name: `${this.authClinicalService.slug}evocall/requests/id`, params: { id: request.id } })
              // window.open(routeData.href, '_blank')
              this.$router.push({ name: `${this.authClinicalService.slug}evocall/requests/id`, params: { id: request.id } })
            }, 250)
          }).catch(errors => {
            this.acceptedCall = false
            if (errors.response.status === 422) {
              this.showToast(this.$t('evocall.requests.actions.request_ongoing'), this.$store.state.errorToastOptions)
            }
          })
      }
      this.showObservationsModal = false
      this.showPriorityModal = false
    },
    closeModal: function () {
      if (!this.wasCanceledByUser) {
        this.$socket.emit('REQUEST_CLOSED', { serviceId: this.request.service_id, requestRow: this.request })
      }
      this.isOpen = false
      setTimeout(() => {
        this.$emit('modal-closed')
      }, 500)
    }
  },
  watch: {
    requests: function (requests) {
      setTimeout(() => {
        if (!this.acceptedCall && !requests.some(req => req.id === this.request.id)) {
          if (!this.wasCanceledByUser) {
            this.showToast(this.$t('evocall.requests.canceled_by_dispenser'), this.$store.state.errorToastOptions)
          }
          this.closeModal()
        }
      }, 1000)
    }
  }
}
</script>

<style lang="scss">

#requestDetailsModal {
  .va-modal__actions {
    justify-content: flex-end;
  }
}

</style>
